import { MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DriverCard } from './DriverCard';
import { TextInput } from '../../components/atomic/TextInput';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { Spinner } from '../../components/Spinner';
import useDebounce from '../../hooks/useDebounce';
import { useInfiniteFetchDrivers } from '../../hooks/useFetchDrivers';
import { t } from '../../services/translation/translation';

export const DriversCards = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const orderBy = searchParams.get('orderBy') ?? 'resume.departureAt';
  const direction = (searchParams.get('direction') ?? 'desc') as 'desc' | 'asc';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteFetchDrivers({
    direction,
    orderBy,
    search: debouncedSearchQuery,
  });

  const drivers = useMemo(() => data?.pages.map(p => p.items).flat() || [], [data]);

  return (
    <div>
      <div className="p-5 text-lg font-semibold text-left text-gray-900 bg-white dark:text-white dark:bg-gray-800">
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            {t('drivers-route.table-title')}
            <p className="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">{t('drivers-route.caption')}</p>
          </div>
          <PrimaryButton text={t('common.add')} onClick={() => navigate('add')} />
        </div>
      </div>
      <TextInput
        placeholder={t('invoicing-route.search-driver')}
        className="min-w-full xl:w-56 xl:min-w-0 mb-3 mt-3"
        value={searchQuery}
        onChange={e => setSearchQuery(e.currentTarget.value)}
        icon={<MagnifyingGlassSolid width={16} height={16} className="fill-current" />}
      />
      <div className="mt-3">
        <div className="space-y-2">
          {drivers.map(driver => (
            <DriverCard key={driver.id} driver={driver} />
          ))}
        </div>

        <div>
          {hasNextPage ? (
            <div className="flex justify-center mt-3">
              <PrimaryButton
                onClick={() => {
                  fetchNextPage();
                }}
                isLoading={isFetchingNextPage}
              >
                {t('common.see-more')}
              </PrimaryButton>
            </div>
          ) : null}
          <div>{isLoading ? <Spinner /> : null}</div>
        </div>
      </div>
    </div>
  );
};
